import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {
    IAllWinData,
    IBetWinData,
    ICarData,
    IConnectedWin,
    ISpecialSymData,
    IWinHighLightData,
    RoadKillGameData,
    RoadKillInitData
} from "./RoadKillData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {RoadKillGameMode} from "./RoadKillGameMode";
import {RoadKillGameConfig} from "../RoadKillGameConfig";
import {DIRECTION, JW_TYPE, SYMBOL_NAME} from "../component/enums/RoadKillEnums";
import {IMovingCarData} from "../gameflow/RESPIN/RoadKillRespinResultPresentation";
import {RoadKillUtils} from "../utils/RoadKillUtils";
import {RoadKill} from "../RoadKill";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {IWinRatioConfigHighLight, RoadKillWinConfigs} from "../RoadKillWinConfigs";

export class RoadKillModel extends GameModel<RoadKillInitData, RoadKillGameData> {
    public isRestore: boolean = false;
    public betWayWins: IBetWinData[];
    public brokeTheBank: boolean;
    public singleWin: number;
    public combinedBetWayWins: IBetWinData[];
    public characterData: Map<JW_TYPE, ISpecialSymData> = new Map<JW_TYPE, ISpecialSymData>();
    public nextMode: string;
    public mode: string
    public freespinsLeft: number = 0;
    public specialSymLandingArray: ISpecialSymData[];
    public symbolSize: number[][];
    public totalAnimalHitsSFS: number;
    public reels: string[][];
    public isLuckyDraw: boolean;

    public allBetWayWinDataHighlight: IAllWinData;
    public winDataHighlight: IWinHighLightData[] = [];
    public normalWinDataHighlight: IWinHighLightData[] = [];

    private _playedBetValue: number;
    private _totalSpinWinnings: number;

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);
    }

    private _isMassiveWin: boolean;

    get isMassiveWin(): boolean {
        return this._isMassiveWin;
    }

    private _allWinPositions: boolean[][];

    public get allWinPositions() {
        return this._allWinPositions;
    }

    private _evaluatedArea: string[][];

    get evaluatedArea(): string[][] {
        return this._evaluatedArea;
    }

    private _accumulatedRoundWin: number;

    get accumulatedRoundWin(): number {
        return this._accumulatedRoundWin;
    }

    private _winRatio: number;

    get winRatio(): number {
        if (this.betWayWins.length === 0) {
            return 0;
        }
        const betWinsData: IBetWinData[] = this.betWayWins;
        let amount: number = 0;
        for (let i: number = 0; i < betWinsData.length; i++) {
            amount += betWinsData[i].multipliedAmount;
        }
        return amount / this._playedBetValue;
    }

    private _isRoundComplete: boolean = true;

    get isRoundComplete(): boolean {
        return this._isRoundComplete;
    }

    public getMovingCarData(): IMovingCarData[] {
        const carDetailsArr: IMovingCarData[] = [];
        this.gameData.carInfo.forEach((carData: ICarData) => {
            const hitByCharData: ISpecialSymData | any = this.characterData.get(RoadKillUtils.getJWType(carData.hitByAnimal));
            const hitByCharData2: ISpecialSymData | any = this.characterData.get(RoadKillUtils.getJWType(carData.hitByAnimal2));
            const hitCharData: ISpecialSymData | any [] = [hitByCharData, hitByCharData2];
            carData.hitCount = 0;
            if (carData.carDirection[0] === DIRECTION.RIGHT && carData.fromRightRow > -1) {
                carDetailsArr.push(this.getParsedCarData(carData, hitCharData, DIRECTION.RIGHT));
            }
            if (carData.carDirection[1] === DIRECTION.LEFT && carData.fromLeftRow > -1) {
                carDetailsArr.push(this.getParsedCarData(carData, hitCharData, DIRECTION.LEFT));
            }
        });
        Logger.logDev("car details : ", carDetailsArr);
        return carDetailsArr;
    }

    public bothCarHitDeerBearMassiveWild(animalName: string): boolean {
        if (this.gameData && !this.gameData.doMassiveWildThisSpin) {
            return false
        }

        let returnVal: boolean = true;
        for (let carIdx = 0; carIdx < this.gameData.carInfo.length; carIdx++) {
            const individualCar = this.gameData.carInfo[carIdx];
            if (individualCar.hitByAnimal !== animalName) {
                returnVal = false;
            }
        }
        return returnVal;

    }

    protected onInit(data: RoadKillInitData): void {
        super.onInit(data);
        this.isRestore = data.isRestoreState;
        this.nextMode = data.nextMode;
        this.mode = data.mode;
        this.freespinsLeft = data.freespinsLeft;
        this._isRoundComplete = data.nextMode === RoadKillGameMode.NORMAL;
        this.parseCharData(data);
        this.specialSymLandingArray = [];
        this.brokeTheBank = false;
        this._isMassiveWin = false;
        this.totalAnimalHitsSFS = data.totalAnimalHitsSFS;
        this._evaluatedArea = data.evaluatedArea;
        this.symbolSize = data.symbolSizes;
        this.reels = data.reels;
        this.isLuckyDraw = data.isLuckyDraw;
    }

    protected onGame(data: RoadKillGameData): void {
        super.onGame(data);

        this.singleWin = data.totalSpinWinnings;
        this.isRestore = false;
        this.brokeTheBank = data.brokeTheBank;
        this.nextMode = data.nextMode;
        this.mode = data.mode;
        this.freespinsLeft = data.freespinsLeft;
        this._evaluatedArea = data.evaluatedArea;
        this._playedBetValue = data.playedBetValue;
        this._accumulatedRoundWin = data.accumulatedRoundWin;
        this._totalSpinWinnings = data.totalSpinWinnings;
        this._isRoundComplete = data.nextMode === RoadKillGameMode.NORMAL;
        this._isMassiveWin = data.doMassiveWildThisSpin;
        this.totalAnimalHitsSFS = data.totalAnimalHitsSFS;
        this.symbolSize = data.symbolSizes;
        this.reels = data.reels;
        this.isLuckyDraw = data.isLuckyDraw;

        //39 sw1 removed from buffer symbols
        for (let reelId: number = 0; reelId < data.reels.length; reelId++) {
            if (RoadKillGameConfig.WILD_SYMBOLS.indexOf(data.reels[reelId][0]) > -1) {
                Logger.logDev("S1 changed in reel in place of ", data.reels[reelId][0], " at ReelId : ", reelId, " Row Id : 0");
                data.reels[reelId][0] = SYMBOL_NAME.SCATTER;
            }
            const lastRow: number = data.reels[reelId].length - 1;
            if (RoadKillGameConfig.WILD_SYMBOLS.indexOf(data.reels[reelId][lastRow]) > -1) {
                Logger.logDev("S1 changed in reel in place of ", data.reels[reelId][lastRow], " at ReelId : ", reelId, " Row Id : ", lastRow);
                data.reels[reelId][lastRow] = SYMBOL_NAME.SCATTER;
            }
        }
        this.parseCharData(data);
        this.betWayWins = this.data.evaluatedArea ? this.createConnectedWinWays(data.betWayWins, this.data.evaluatedArea) : [];
        this.combinedBetWayWins = this.combineWinData(this.betWayWins);
        this.calculateAllWinPosition();
        this.specialSymLandingArray = this.fillSpecialSymLandingArray(this.characterData);

        //below three variable are being used only for working of win highlights animations
        this.winDataHighlight = this.addBetLvls(this.createConnectedWinWaysForWinHightlight(JSON.parse(JSON.stringify(data.betWayWins)), JSON.parse(JSON.stringify(data.evaluatedArea))), this._playedBetValue, RoadKillWinConfigs.WIN_RATIOS!.normalWin);
        this.allBetWayWinDataHighlight = this.parseAllHits(this.winDataHighlight, this.data.reels.length, 3);
        this.normalWinDataHighlight = this.combineWinDataHighLight(this.winDataHighlight);
    }

    protected addBetLvls(data: IWinHighLightData[], betAmount: number, winRatios: IWinRatioConfigHighLight[]): IWinHighLightData[] {

        const res: IWinHighLightData[] = [];

        for (let d: number = 0; d < data.length; d++) {
            const betGrp: IWinHighLightData = <IWinHighLightData>data[d];

            let winLvl: number = 0;
            const amountRatio: number = data[d].amount / betAmount;
            winRatios.forEach((v, i) => v.ratio <= amountRatio ? winLvl = i : winLvl)

            betGrp.reelWinLvlPositions = [];
            betGrp.winLvl = winLvl;
            for (let reel: number = 0; reel < data[d].reelWinPositions.length; reel++) {
                betGrp.reelWinLvlPositions[reel] = [];
                for (let row: number = 0; row < data[d].reelWinPositions[reel].length; row++) {
                    betGrp.reelWinLvlPositions[reel][row] = data[d].reelWinPositions[reel][row] ? winLvl : -1;
                }
            }

            res.push(betGrp);
        }

        res.sort((a, b) => {
            return a.amount - b.amount;
        });

        return res;
    }

    protected combineWinDataHighLight(data: IWinHighLightData[]): IWinHighLightData[] {
        const temp: IWinHighLightData[] = [];

        data.forEach(d => {
            let indxToFill: number = temp.findIndex(v => v.symbol == d.symbol);
            if (indxToFill == -1) {
                indxToFill = temp.push({
                    amount: d.amount,
                    length: d.length,
                    multipliedAmount: d.multipliedAmount,
                    multiplier: d.multiplier,
                    numberOfCombinations: d.numberOfCombinations,
                    reelWinLvlPositions:
                        RoadKillUtils.initArrayWithValues(d.reelWinLvlPositions.length, (l) =>
                            RoadKillUtils.initArrayWithValues(d.reelWinLvlPositions[l].length, (k) => d.reelWinLvlPositions[l][k])),
                    reelWinPositions:
                        RoadKillUtils.initArrayWithValues(d.reelWinPositions.length, (l) =>
                            RoadKillUtils.initArrayWithValues(d.reelWinPositions[l].length, (k) => d.reelWinPositions[l][k])),
                    symbol: d.symbol,
                    winLvl: d.winLvl
                });
                return;
            }

            temp[indxToFill].amount += d.amount;
            temp[indxToFill].length = d.length > temp[indxToFill].length ? d.length : temp[indxToFill].length;
            temp[indxToFill].multipliedAmount += d.multipliedAmount;
            temp[indxToFill].multiplier = d.multiplier;
            temp[indxToFill].numberOfCombinations += d.numberOfCombinations;
            RoadKillUtils.eachForEach(temp[indxToFill].reelWinPositions, (reel, row) => {
                if (d.reelWinPositions[reel][row]) temp[indxToFill].reelWinPositions[reel][row] = true;
            })
        });
        temp.forEach(t => {
            let winLvl: number = 0;
            const amountRatio: number = t.amount / this._playedBetValue;
            RoadKillWinConfigs.WIN_RATIOS!.normalWin.forEach((v, i) => v.ratio <= amountRatio ? winLvl = i : winLvl)!
            t.winLvl = winLvl;

            RoadKillUtils.eachForEach(t.reelWinLvlPositions, (reel, row) => {
                if (t.reelWinPositions[reel][row]) t.reelWinLvlPositions[reel][row] = winLvl;
            })
        })

        return temp;
    }

    protected parseAllHits(betW: IWinHighLightData[], amountOfReels: number, reelHeights: number | number[]): IAllWinData {
        let allWinData: IAllWinData = {
            symbols: [],
            longest: 0,
            reelWinPositions: [],
            reelWinLvlPositions: [],
            totalWinCombination: 0,
            biggestWinLvl: 0,
            multiplier: 1,
        };

        for (let r: number = 0; r < amountOfReels; r++) {
            allWinData.reelWinPositions.push([])
            allWinData.reelWinLvlPositions.push([])
            for (let j: number = 0; j < RoadKillUtils.fetchArrTypeValue(reelHeights, r); j++) {
                allWinData.reelWinPositions[r].push(false);
                allWinData.reelWinLvlPositions[r].push(-1);
            }
        }

        for (let groups = 0; groups < betW!.length; groups++) {
            const winData: IWinHighLightData = betW![groups];

            allWinData.symbols.push(winData.symbol);
            allWinData.totalWinCombination += winData.numberOfCombinations;
            allWinData.multiplier = winData.multiplier;
            if (winData.length > allWinData.longest) allWinData.longest = winData.length;

            for (let reel = 0; reel < winData.reelWinPositions.length; reel++) {
                let posRow: boolean[] = winData.reelWinPositions[reel];
                for (let row = 0; row < posRow.length; row++) {
                    if (posRow[row]) {
                        allWinData.reelWinPositions[reel][row] = posRow[row];
                        allWinData.reelWinLvlPositions[reel][row] = winData.reelWinLvlPositions[reel][row];
                        if (allWinData.biggestWinLvl < winData.reelWinLvlPositions[reel][row]) allWinData.biggestWinLvl = winData.reelWinLvlPositions[reel][row];
                    }
                }
            }
        }

        return allWinData;
    }

    protected applyWinHighLightDataForConnected(data: IBetWinData[], gen: IConnectedWin[]): IWinHighLightData[] {
        //only for win high light purpose
        const completedArr: IWinHighLightData[] = [];

        data.forEach(d => {
            const fetched: IConnectedWin | undefined = gen.find(g => g.symbol === d.symbol && g.groups[d.length]);

            if (fetched) {
                const dataToFill: IWinHighLightData = createNewBlock(fetched.symbol);

                dataToFill.amount = d.amount;
                dataToFill.length = d.length;
                dataToFill.numberOfCombinations = d.numberOfCombinations;
                dataToFill.multipliedAmount = d.multipliedAmount;
                dataToFill.multiplier = d.multiplier;
                dataToFill.reelWinPositions = convToBooleanMap(fetched.groups[d.length])
                completedArr.push(dataToFill);
            }
        });


        function convToBooleanMap(arr: number[][]): boolean[][] {
            return RoadKillUtils.initArrayWithValues(arr.length, (i) => RoadKillUtils.initArrayWithValues(arr[i].length, (k) => !!arr[i][k]))
        }


        function createNewBlock(type: string): IWinHighLightData {
            return {
                symbol: type,
                amount: 0,
                length: 0,
                winLvl: 0,
                multiplier: 0,
                multipliedAmount: 0,
                numberOfCombinations: 0,
                reelWinPositions: [],
                reelWinLvlPositions: []
            };
        }

        return completedArr;
    }

    protected createConnectedWinWays(data: IBetWinData[], evaluatedReels: string[][]): IBetWinData[] {
        const gen: IConnectedWin[] = [];
        const specialSymbolList: string[] = this.animalsPartsOfWinning();
        //making hard copy of the data as of now for escaping any kind of change in original data
        let betWinData: IBetWinData[] = [];
        gen.push(...this.generateConnectedWinWays(evaluatedReels, specialSymbolList));
        data.forEach((individualBetWin) => {
            betWinData.push(individualBetWin);
        })
        const res: IBetWinData[] = this.applyWinDataForConnected(betWinData, gen);
        return res;
    }

    private animalsPartsOfWinning(): string[] {
        let specialSymbolList: string[] = ["W"];
        this.characterData.forEach((specialSymData) => {
            if (specialSymData.coordTo) {
                specialSymbolList.push(specialSymData.symName);
            }
        });
        return specialSymbolList;
    }

    private createConnectedWinWaysForWinHightlight(data: IBetWinData[], reels: string[][]): IWinHighLightData[] {
        //only for win high light purpose
        const gen: IConnectedWin[] = [],
            specialSymbolList: string[] = this.animalsPartsOfWinning();


        gen.push(...this.generateConnectedWinWays(reels, specialSymbolList))
        const res: IWinHighLightData[] = this.applyWinHighLightDataForConnected(data, gen);
        return res;
    }

    private getParsedCarData(carData: ICarData, hitByCharData: ISpecialSymData[], direction: DIRECTION): IMovingCarData {
        const movingCarData: IMovingCarData = {
            name: carData.carName,
            rowId: carData.fromRightRow,
            sourceReelId: -1,
            destReelIds: [],
            direction: direction,
            hpLeft: [],
        };
        if (movingCarData.direction === DIRECTION.RIGHT) {
            if (this.isMassiveWin) {
                if (hitByCharData[carData.hitCount]) {
                    movingCarData.animalData = [hitByCharData[carData.hitCount]];
                    carData.hitCount++;
                    movingCarData.destReelIds = [carData.reelHitId];
                    movingCarData.hitByAnimal = [carData.hitByAnimal];
                }
            } else {
                for (let hitIndex: number = 1; hitIndex <= carData.animalHitCountFromRight; hitIndex++) {
                    this.setMovingCarData(movingCarData, hitByCharData[carData.hitCount], carData);
                }
            }
            movingCarData.sourceReelId = 4;
            movingCarData.rowId = carData.fromRightRow;
        }
        if (movingCarData.direction === DIRECTION.LEFT) {
            for (let hitIndex: number = 1; hitIndex <= carData.animalHitCountFromLeft; hitIndex++) {
                this.setMovingCarData(movingCarData, hitByCharData[carData.hitCount], carData);
            }
            movingCarData.sourceReelId = 0;
            movingCarData.rowId = carData.fromLeftRow;
        }

        if (!movingCarData.destReelIds.length) {
            movingCarData.destReelIds.push(-1);
        }
        if (movingCarData.hpLeft.length && movingCarData.hpLeft[movingCarData.hpLeft.length - 1] > 0 && movingCarData.destReelIds[movingCarData.destReelIds.length - 1] !== -1) {
            movingCarData.destReelIds.push(-1);
        }

        return movingCarData;
    }

    private setMovingCarData(movingCarData: IMovingCarData, animalData: ISpecialSymData, carData: ICarData): void {
        const hp: number = carData.carName === "Red" ? 2 : 1;
        movingCarData.animalData = movingCarData.animalData || [];
        movingCarData.animalData.push(animalData);
        carData.hitCount++;
        if (animalData.symName === SYMBOL_NAME.SW1) {
            movingCarData.hpLeft.push(0);
        } else {
            movingCarData.hpLeft.push(hp - carData.hitCount);
        }
        movingCarData.destReelIds.push(carData.hitCount === 2 ? carData.reelHitId2 : carData.reelHitId);
        movingCarData.hitByAnimal = movingCarData.hitByAnimal || [];
        movingCarData.hitByAnimal.push(carData.hitCount === 2 ? carData.hitByAnimal2 : carData.hitByAnimal);
    }

    private fillSpecialSymLandingArray(characterData: Map<JW_TYPE, ISpecialSymData>): ISpecialSymData[] {
        const returnArray: ISpecialSymData[] = [];
        characterData.forEach((data) => {
            if (!data.coordTo) {
                returnArray.push(data);
            }
        });
        return returnArray;
    }

    private combineWinData(data: IBetWinData[]): IBetWinData[] {
        const temp: IBetWinData[] = [];

        data.forEach(d => {
            let indxToFill: number = temp.findIndex(v => v.symbol == d.symbol);
            if (indxToFill == -1) {
                indxToFill = temp.push({
                    amount: d.amount,
                    length: d.length,
                    multipliedAmount: d.multipliedAmount,
                    multiplier: d.multiplier,
                    numberOfCombinations: d.numberOfCombinations,
                    winPositions:
                        RoadKillUtils.initArrayWithValues(d.winPositions.length, (l) =>
                            RoadKillUtils.initArrayWithValues(d.winPositions[l].length, (k) => d.winPositions[l][k])),
                    symbol: d.symbol,
                });
                return;
            }

            temp[indxToFill].amount += d.amount;
            temp[indxToFill].length = d.length > temp[indxToFill].length ? d.length : temp[indxToFill].length;
            temp[indxToFill].multipliedAmount += d.multipliedAmount;
            temp[indxToFill].multiplier = d.multiplier;
            temp[indxToFill].numberOfCombinations += d.numberOfCombinations;
            RoadKillUtils.eachForEach(temp[indxToFill].winPositions, (reel, row) => {
                if (d.winPositions[reel][row]) temp[indxToFill].winPositions[reel][row] = true;
            })
        });
        return temp;
    }

    private applyWinDataForConnected(data: IBetWinData[], gen: IConnectedWin[]): IBetWinData[] {
        const completedArr: IBetWinData[] = [];

        data.forEach(d => {
            const fetched: IConnectedWin | undefined = gen.find(g => g.symbol === d.symbol && g.groups[d.length]);

            if (fetched) {
                const dataToFill: IBetWinData = createNewBlock(fetched.symbol);

                dataToFill.amount = d.amount;
                dataToFill.length = d.length;
                dataToFill.numberOfCombinations = d.numberOfCombinations;
                dataToFill.multipliedAmount = d.multipliedAmount;
                dataToFill.multiplier = d.multiplier;
                dataToFill.winPositions = convToBooleanMap(fetched.groups[d.length])
                completedArr.push(dataToFill);
            }
        });


        function convToBooleanMap(arr: number[][]): boolean[][] {
            return RoadKillUtils.initArrayWithValues(arr.length, (i) => RoadKillUtils.initArrayWithValues(arr[i].length, (k) => !!arr[i][k]))
        }


        function createNewBlock(type: string): IBetWinData {
            return {
                symbol: type,
                amount: 0,
                length: 0,
                multiplier: 0,
                multipliedAmount: 0,
                numberOfCombinations: 0,
                winPositions: [],
            };
        }

        return completedArr;
    }

    private generateConnectedWinWays(windowReel: string[][], specialSymbols: string[]): IConnectedWin[] {
        const res: IConnectedWin[] = [];
        const minWinLength: number = 2;
        const maxLength: number = 5;
        let firstSymbols: string[][] = RoadKillUtils.initArrayWithValues(windowReel[0].length, (i) => [windowReel[0][i]]);

        for (let startIndx: number = 0; startIndx < firstSymbols.length; startIndx++) {
            for (let secDim: number = 0; secDim < firstSymbols[startIndx].length; secDim++) {
                const toCheck: string = firstSymbols[startIndx][secDim];

                const connected: boolean[][] = [RoadKillUtils.initArrayWithValues(firstSymbols.length, (i) => i === startIndx)];

                //Find connections FromStart
                for (let reel: number = 1; reel < windowReel.length; reel++) {
                    const connectedValues = checkConnected(connected[reel - 1], windowReel[reel], [toCheck, ...specialSymbols]);
                    if (connectedValues.indexOf(!0) == -1) break;
                    connected.push(connectedValues);
                }

                if (connected.length < 3 || (connected.length == 3 && connected[2].indexOf(!0) == -1)) { // ignore
                    continue;
                }

                // Remove nonPossibleConnections, less than minWinLength will be removed.
                connected[minWinLength - 1].forEach((v, i) => {
                    if (v) {
                        let isPossible: boolean = false;
                        connectCheck(i, connected[minWinLength - 1].length, (checkIndx) => {
                            if (connected[minWinLength][checkIndx]) isPossible = true;
                        });
                        connected[minWinLength - 1][i] = isPossible;
                    }
                })

                // Create Weighted map
                const weightM: number[][] = RoadKillUtils.initArrayWithValues(connected.length, (k) => RoadKillUtils.initArrayWithValues(connected[k].length, (j) => {
                        return connected[k][j] ? k + 1 : 0
                    }
                ));

                let currWinData: IConnectedWin | undefined = res.find((v) => toCheck === v.symbol);
                if (!currWinData) {
                    res.push({
                        symbol: toCheck,
                        groups: {},
                    })
                    currWinData = RoadKillUtils.fetchLastValue(res);
                }

                let collectedPaths: number[][] = [];
                const prevMapping: number[][] = [];

                for (let lastIndx: number = weightM.length - 1; lastIndx >= minWinLength; lastIndx--) {
                    for (let k = 0; k < weightM[lastIndx].length; k++) {
                        const currLength: number = lastIndx + 1;
                        let isEndPoint = weightM[lastIndx][k] === currLength;

                        if (isEndPoint) {
                            if (prevMapping[lastIndx] === undefined) {
                                prevMapping[lastIndx] = RoadKillUtils.initArrayWithValues(weightM[lastIndx].length, (i) => weightM[lastIndx][i] === currLength ? currLength : 0);
                                collectedPaths.push(prevMapping[lastIndx]);
                            } else {
                                if (prevMapping[lastIndx][k]) continue;
                                prevMapping[lastIndx][k] = currLength;
                                collectedPaths.push(prevMapping[lastIndx].map(v => v === currLength ? currLength : 0));
                            }
                            for (let rev: number = lastIndx; rev > 0; rev--) {
                                const connectedValues = weightConnection(prevMapping[rev], weightM[rev - 1], currLength, rev + 1);
                                if (prevMapping[rev - 1] == undefined) prevMapping[rev - 1] = [...connectedValues];
                                else {
                                    connectedValues.forEach((v, o) => {
                                        if (v > prevMapping[rev - 1][o]) prevMapping[rev - 1][o] = v
                                    })
                                }
                                collectedPaths.push(connectedValues);
                            }
                        }
                    }
                }

                collectedPaths = collectedPaths.reverse();

                for (let winLength: number = minWinLength + 1; winLength <= maxLength; winLength++) {
                    mergeWeightedPos(currWinData, collectedPaths, winLength)
                }
            }
        }

        // Weight check.
        res.forEach(val => {
            Object.keys(val.groups).forEach(key => {
                if (key !== "5") {
                    for (let keyIndx: number = 0; keyIndx < val.groups[key].length - 1; keyIndx++) {
                        for (let rowIndx: number = 0; rowIndx < val.groups[key][keyIndx].length; rowIndx++) {
                            let isPossible: boolean = false;
                            connectCheck(rowIndx, val.groups[key][keyIndx + 1].length, (checkIndx) => {
                                if (val.groups[key][keyIndx + 1][checkIndx]) {
                                    isPossible = true;
                                }
                            })
                            if (!isPossible && val.groups[key][keyIndx][rowIndx]) {
                                val.groups[key][keyIndx][rowIndx] = 0;
                            }
                        }
                    }
                }

                for (let inc: number = windowReel.length - val.groups[key].length; inc; inc--)
                    val.groups[key].push(RoadKillUtils.initArrayWithValues(windowReel[windowReel.length - inc].length, () => 0));
            })
        });

        function mergeWeightedPos(currWinData: IConnectedWin, array: number[][], lengthValue: number) {

            const filtArr: number[][] = array.filter((v) => v.indexOf(lengthValue) >= 0);
            if (!filtArr.length) return;

            if (!currWinData.groups[lengthValue]) currWinData.groups[lengthValue] = [];
            // mergeWeightedPos(currWinData.fyra.concat(collectedPaths), 4)

            currWinData.groups[lengthValue] = currWinData.groups[lengthValue].concat(filtArr);

            const template: number[][] = RoadKillUtils.initArrayWithValues(lengthValue, () => RoadKillUtils.initArrayWithValues(array[0].length, () => 0));

            RoadKillUtils.eachForEach(currWinData.groups[lengthValue], (reel, row, v) => {
                if (v) template[reel % lengthValue][row] = v;
            })
            currWinData.groups[lengthValue] = template;
        }

        function weightConnection(prevConnected: number[], path: number[], highValue: number, reelValue: number): number[] {

            let minorMap: number[] = RoadKillUtils.initArrayWithValues(prevConnected.length, () => 0);

            prevConnected.forEach((v, i) => {

                if (v) {
                    if (highValue === reelValue && reelValue < v) return;
                    connectCheck(i, prevConnected.length, (checkIndx) => {
                        minorMap[checkIndx] = highValue > path[checkIndx] && path[checkIndx] ? highValue : 0;
                    })
                }
            })

            return minorMap;
        }

        function checkConnected(prevConnected: boolean[], reel: string[], symbolType: string[]): boolean[] {

            let minorMap: boolean[] = RoadKillUtils.initArrayWithValues(prevConnected.length, () => false);

            prevConnected.forEach((v, i) => {
                if (v) {
                    connectCheck(i, prevConnected.length, (checkIndx) => {
                        minorMap[checkIndx] = checker(reel[checkIndx], symbolType);
                    })
                }
            })
            return minorMap;
        }


        function connectCheck(startIndx: number, maxHeight: number, func: (indx: number) => void): void {
            const startPos: number = Math.max(0, startIndx - 1); // Forward-Up
            const endPos: number = Math.min(maxHeight - 1, startIndx + 1); // Forward-Down
            for (let checkIndx: number = startPos; checkIndx <= endPos; checkIndx++) func(checkIndx)
        }

        function checker(symbol: string, target: string[]): boolean {
            return !!target.find(v => v === symbol);
        }

        return res;
    }

    private parseCharData(data: RoadKillInitData | RoadKillGameData): void {
        this.characterData.clear();
        this.data.specialSymbolList?.forEach((specSymData: ISpecialSymData) => {
            this.characterData.set(RoadKillUtils.getJWType(specSymData.symName), specSymData);
            if (specSymData.rabbitTrap?.length) {
                const trapData: ISpecialSymData = Object.assign({}, specSymData);
                trapData.symName = "Trap";
                this.characterData.set(RoadKillUtils.getJWType("Trap"), trapData);
            }
            if (!("symbolValues" in this.data)) {
                if (!specSymData.coordTo) {
                    if (RoadKillGameConfig.WILD_SYMBOLS.indexOf(data.reels[specSymData.coordFrom[0]][specSymData.coordFrom[1] + 1]) > -1) {
                        data.reels[specSymData.coordFrom[0]][specSymData.coordFrom[1] + 1] = SYMBOL_NAME.SCATTER;
                    }
                }
            }
        });
    }

    private calculateAllWinPosition(): void {
        if (this.betWayWins.length) {
            this._allWinPositions = [];
            this.betWayWins.forEach((betWayWinData: IBetWinData, index: number) => {
                betWayWinData.winPositions.forEach((reels: boolean[], reelId: number) => {
                    this._allWinPositions[reelId] = this._allWinPositions[reelId] || [];
                    if (index === 0) {
                        this._allWinPositions[reelId] = this._allWinPositions[reelId].concat(reels);
                    } else {
                        reels.forEach((isWin: boolean, rowId: number) => {
                            this._allWinPositions[reelId][rowId] = this._allWinPositions[reelId][rowId] || isWin;
                        });
                    }
                });
            });
        } else {
            this._allWinPositions = this.defaultAllWinPos();
        }
    }

    private defaultAllWinPos(): boolean[][] {
        const defaultWinPos: boolean[][] = [];
        this.evaluatedArea.forEach((reels: string[], reelId: number) => {
            defaultWinPos[reelId] = [];
            reels.forEach((sym: string, rowId: number) => {
                defaultWinPos[reelId].push(false);
            });
        });
        return defaultWinPos;
    }
}

